<template>
  <div
    class="ccgd-card ccgd-card--app"
    :class="{ selected: isSelected, 'ccgd-disabled': !canOpen }"
    @click="onRowClick"
  >
    <div class="ccgd-accent" />

    <div class="ccgd-main">
      <div class="ccgd-header">
        <div class="ccgd-title-wrap">
          <div class="ccgd-title-row ccgd-title-row--app">
            <div class="ccgd-title">
              Application
              <span class="ccgd-muted">•</span>
              {{ borrowerName }}
            </div>

            <div class="ccgd-header-meta">
              <div class="ccgd-meta-item-inline" v-if="loanOfficerName">
                <i class="fa-duotone fa-user-tie ccgd-ico ccgd-ico--lo"></i>
                <span class="ccgd-meta-text">{{ loanOfficerName }}</span>
              </div>

              <div class="ccgd-meta-item-inline" v-else-if="loanNumber">
                <i class="fa-duotone fa-hashtag ccgd-ico ccgd-ico--loan"></i>
                <span class="ccgd-meta-text">{{ loanNumber }}</span>
              </div>

              <div class="ccgd-meta-item-inline" v-if="orgId">
                <i class="fa-duotone fa-building ccgd-ico ccgd-ico--org"></i>
                <span class="ccgd-meta-text">{{ orgId }}</span>
              </div>
            </div>
          </div>

          <div class="ccgd-details">
            <div class="ccgd-detail-row" v-if="streetAddress">
              <i class="fa-duotone fa-map ccgd-ico ccgd-ico--addr"></i>
              <span>{{ streetAddress }}</span>
              <span v-if="state" class="ccgd-muted">• {{ state }}</span>
            </div>

            <div class="ccgd-detail-row" v-if="coBorrowerName">
              <i class="fa-duotone fa-user-group ccgd-ico ccgd-ico--co"></i>
              <span>Co-borrower: {{ coBorrowerName }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="ccgd-footer-row">
        <div class="ccgd-date-pill">
          <i class="fa-duotone fa-calendar ccgd-ico ccgd-ico--date"></i>
          <span class="ccgd-date-pill-label">Application Date</span>
          <span class="ccgd-date-pill-value">{{ appDateLabel }}</span>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: "ApplicationCard",
  props: {
    item: { type: Object, required: true },
    selectedField: { type: String, default: "selected" }
  },
  computed: {
    isSelected() {
      return !!(this.item && this.selectedField && this.item[this.selectedField]);
    },
    borrowerName() {
      return this.clean(this.item.borrowername || this.item.borrower_name) || "—";
    },
    coBorrowerName() {
      return this.clean(this.item.coborrowername || this.item.coborrower_name);
    },
    loanOfficerName() {
      return this.clean(this.item.loanofficername || this.item.loanofficer_name);
    },
    loanNumber() {
      return this.clean(this.item.f364);
    },
    orgId() {
      return this.clean(this.item.orgid);
    },
    state() {
      return this.clean(this.item.state);
    },
    streetAddress() {
      const s1 = this.clean(this.item.streetaddress);
      if (s1 && s1.toUpperCase() !== "TBD") return s1;

      const s2 = this.clean(this.item.f11);
      if (s2 && s2.toUpperCase() !== "TBD") return s2;

      return null;
    },
    appDateLabel() {
      return this.formatDate(this.item.appdate || this.item.logmsdatestarted);
    },
    canOpen() {
      if (!this.item) return false;
      const hasAppId = !!this.clean(this.item.appid);
      const hasId = !!this.clean(this.item.id);
      return  hasId;
    }
  },
  methods: {
    onRowClick() {
      if (!this.canOpen) return;
      this.$emit("row-click", this.item);
    },
    clean(v) {
      if (v == null) return null;
      const s = String(v).trim();
      if (!s) return null;
      const low = s.toLowerCase();
      if (low === "undefined" || low === "null") return null;
      return s;
    },
    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
    }
  }
};
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.ccgd-main,
.ccgd-header,
.ccgd-title-wrap,
.ccgd-title-row--app {
  width: 100%;
}

.ccgd-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.ccgd-title-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.ccgd-title-row--app {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.ccgd-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccgd-header-meta {
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  white-space: nowrap;
}

.ccgd-meta-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.70);
}

.ccgd-meta-text {
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccgd-details {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ccgd-detail-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: rgba(15, 23, 42, 0.75);
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;

  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}

.ccgd-ico--addr,
.ccgd-ico--co,
.ccgd-ico--lo,
.ccgd-ico--org,
.ccgd-ico--loan,
.ccgd-ico--date {
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}


.ccgd-footer-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.ccgd-date-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.75);
  width: fit-content;
  max-width: 100%;
}

.ccgd-date-pill-label {
  opacity: 0.75;
  font-weight: 900;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  font-size: 11px;
}

.ccgd-date-pill-value {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.90);
}

.ccgd-disabled {
  cursor: default;
  opacity: 0.88;
}
</style>
